import React from 'react';
import { Text, mergeClassNames } from '@clockout-dev/design-elements';
import { EditOutlined, DeleteOutlined, GiftOutlined, TeamOutlined } from '@ant-design/icons';
import { Popconfirm, Tooltip } from 'antd';
import { formatPromoRuleSentence } from './freeAdvances.constants';
import styles from './styles/freeAdvances.module.scss';

/**
 * One row in the Groups list — a single-line card: brand icon tile, bold name,
 * the rule as a plain-language sentence (composed the same way General's copy
 * is) that ellipsizes rather than wrapping, the active-member count as a green
 * chip that opens the drawer straight to its member roster, then edit / delete.
 *
 * The row never wraps: name and rule share the elastic middle and truncate with
 * a `title` tooltip, so a long label or a long window sentence can't push the
 * count and the actions onto their own lines.
 *
 * Plain `<button>`s rather than design-elements' `Button` here: the chip and the
 * icon buttons are bespoke shapes, and `Button`'s `width: 100%` base would need
 * an `!important` override on every rule (see RulesPanel's `.addRowButton`).
 */
function PromoGroupRow({ group, onOpenMembers, onEdit, onDelete }) {
    const { id, label, active_member_count = 0, enabled = true } = group;
    const rule = formatPromoRuleSentence(group);
    const memberLabel = `${active_member_count} ${active_member_count === 1 ? 'client' : 'clients'}`;

    return (
        <div className={mergeClassNames([styles.groupRow, !enabled ? styles.groupRowPaused : null])}>
            <span className={styles.groupIcon} aria-hidden="true">
                <GiftOutlined />
            </span>

            <div className={styles.groupCopy}>
                <Text as="span" size="s" className={styles.groupName} title={label}>
                    {label}
                </Text>
                <Text as="span" size="s" className={styles.groupRule} title={rule}>
                    {rule}
                </Text>
            </div>

            {!enabled ? <span className={styles.groupPausedPill}>Paused</span> : null}

            <Tooltip title="View members">
                <button
                    type="button"
                    className={styles.groupMemberCount}
                    onClick={() => onOpenMembers(id)}
                    aria-label={`View the ${memberLabel} in ${label}`}
                >
                    <TeamOutlined />
                    {memberLabel}
                </button>
            </Tooltip>

            <div className={styles.groupActions}>
                <Tooltip title="Edit group">
                    <button
                        type="button"
                        className={styles.iconButton}
                        aria-label={`Edit ${label}`}
                        onClick={() => onEdit(id)}
                    >
                        <EditOutlined />
                    </button>
                </Tooltip>
                <Popconfirm
                    title="Delete this group?"
                    description="Its members lose the promotion immediately; their usage history is kept."
                    okText="Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    placement="topRight"
                    onConfirm={() => onDelete(id)}
                >
                    <button
                        type="button"
                        className={mergeClassNames([styles.iconButton, styles.iconButtonDanger])}
                        aria-label={`Delete ${label}`}
                    >
                        <DeleteOutlined />
                    </button>
                </Popconfirm>
            </div>
        </div>
    );
}

export default PromoGroupRow;
